import {
  pageRange,
  PICKER_PAGE_SIZE,
  type DocumentNeighbours,
  type GrantablePermission,
} from '@palaccount/contracts';
import { useQuery } from '@tanstack/react-query';
import { ChevronsLeft, ChevronsRight, Plus, Search } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { api } from '@/api';
import { Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/features/inventory/modal';
import { throwApiError } from '@/lib/server-messages';
import { useDebounced } from '@/lib/typeahead';

/**
 * S4 — THE ONE DOCUMENT HEADER CONTROL SET, used by every document editor
 * through `DocumentToolbar`'s `header` prop rather than copied per screen:
 *
 *   ‹‹ ››   step to the previous / next record in number order (INV-H2, IT-N1)
 *   Go to   type a number or code and press Enter (INV-H9, IT-N7)
 *   Find    a compact, paged, searchable list of the same kind (INV-H3, IT-N2)
 *   New     the kind's /new route, gated by its create permission (INV-H4, IT-N5)
 *
 * What a KIND is, is written down once in `DOCUMENT_KINDS` below: where its
 * records live, which permission creates one, and the three reads the rail
 * needs (neighbours, lookup, a searchable page). The screens pass a kind and
 * an id and nothing else, which is what keeps the seven editors from drifting
 * into seven rails.
 *
 * ── UNSAVED CHANGES ────────────────────────────────────────────────────────
 *
 * The arrows are DISABLED while the editor is dirty, with the reason in the
 * tooltip: stepping away from typed work is how a negotiated price vanishes
 * without a word (GEN-35 names the prompt; a rail that refuses to move is the
 * quieter answer, and the Cancel link beside it still leaves). The editors
 * derive `dirty` from `useUnsavedChanges` below — a snapshot compared against
 * the one taken when the stored document was last adopted.
 */

export type DocumentKind =
  | 'invoice'
  | 'quotation'
  | 'salesOrder'
  | 'bill'
  | 'purchaseOrder'
  | 'goodsReceipt'
  // Wave 2 of specs/erp-gap-analysis.md — the four that had no rail.
  | 'deliveryNote'
  | 'creditNote'
  | 'purchaseReturn'
  | 'importOrder'
  | 'item';

export interface DocumentHeaderSpec {
  kind: DocumentKind;
  /** The record on screen; undefined on a NEW document, where the rail has no position. */
  id: string | undefined;
  /** Typed work not yet saved — the arrows wait for it. */
  dirty: boolean;
}

/** One row of the Find list: enough to recognise a record, nothing to total. */
export interface FindRow {
  id: string;
  code: string;
  name: string;
  date: string | null;
}

interface FindPage {
  rows: FindRow[];
  total: number;
  pageSize: number;
}

interface KindSpec {
  route: (id: string) => string;
  newPath: string;
  permission: GrantablePermission;
  reasonKey: string;
  neighbours: (id: string) => Promise<DocumentNeighbours>;
  /** The id for a printed number, or null for a 404 — anything else throws. */
  lookup: (code: string) => Promise<string | null>;
  /** One page of the kind, narrowed by `q` on the SERVER — every list route takes one. */
  search: (q: string, page: number) => Promise<FindPage>;
}

function lookupId(response: { status: number; body: unknown }): string | null {
  if (response.status === 200) return (response.body as { id: string }).id;
  if (response.status === 404) return null;
  return throwApiError(response.status, response.body);
}

function page<T>(response: { status: number; body: unknown }, rows: (body: T) => FindRow[]): FindPage {
  if (response.status !== 200) throwApiError(response.status, response.body);
  const body = response.body as T & { total: number; pageSize: number };
  return { rows: rows(body), total: body.total, pageSize: body.pageSize };
}

const PAGE = { pageSize: PICKER_PAGE_SIZE } as const;

export const DOCUMENT_KINDS: Record<DocumentKind, KindSpec> = {
  invoice: {
    route: (id) => `/sales/${id}`,
    newPath: '/sales/new',
    permission: 'sales.create',
    reasonKey: 'perm.cannot.createInvoices',
    neighbours: async (id) => {
      const r = await api.invoices.neighbours({ params: { invoiceId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.invoices.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.invoices.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        invoices: { id: string; code: string; partnerName: string; invoiceDate: string }[];
      }) => b.invoices.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.invoiceDate }))),
  },
  quotation: {
    route: (id) => `/sales/quotes/${id}`,
    newPath: '/sales/quotes/new',
    permission: 'sales.create',
    reasonKey: 'perm.cannot.createQuotes',
    neighbours: async (id) => {
      const r = await api.quotations.neighbours({ params: { quotationId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.quotations.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.quotations.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        quotations: { id: string; code: string; partnerName: string; quotationDate: string }[];
      }) => b.quotations.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.quotationDate }))),
  },
  salesOrder: {
    route: (id) => `/sales/orders/${id}/edit`,
    newPath: '/sales/orders/new',
    permission: 'sales.create',
    reasonKey: 'perm.cannot.createSalesOrders',
    neighbours: async (id) => {
      const r = await api.salesOrders.neighbours({ params: { orderId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.salesOrders.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.salesOrders.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        orders: { id: string; code: string; partnerName: string; orderDate: string }[];
      }) => b.orders.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.orderDate }))),
  },
  bill: {
    route: (id) => `/purchases/bills/${id}`,
    newPath: '/purchases/bills/new',
    permission: 'purchasing.create',
    reasonKey: 'perm.cannot.createBills',
    neighbours: async (id) => {
      const r = await api.purchaseInvoices.neighbours({ params: { billId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.purchaseInvoices.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.purchaseInvoices.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        bills: { id: string; code: string; partnerName: string; invoiceDate: string }[];
      }) => b.bills.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.invoiceDate }))),
  },
  purchaseOrder: {
    route: (id) => `/purchases/orders/${id}`,
    newPath: '/purchases/orders/new',
    permission: 'purchasing.create',
    reasonKey: 'perm.cannot.createPurchaseOrders',
    neighbours: async (id) => {
      const r = await api.purchaseOrders.neighbours({ params: { orderId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.purchaseOrders.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.purchaseOrders.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        orders: { id: string; code: string; partnerName: string; orderDate: string }[];
      }) => b.orders.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.orderDate }))),
  },
  goodsReceipt: {
    route: (id) => `/purchases/receipts/${id}`,
    newPath: '/purchases/receipts/new',
    permission: 'purchasing.create',
    reasonKey: 'perm.cannot.createReceipts',
    neighbours: async (id) => {
      const r = await api.goodsReceipts.neighbours({ params: { receiptId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.goodsReceipts.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.goodsReceipts.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        receipts: { id: string; code: string; partnerName: string; receiptDate: string }[];
      }) => b.receipts.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.receiptDate }))),
  },
  deliveryNote: {
    route: (id) => `/sales/deliveries/${id}`,
    newPath: '/sales/deliveries/new',
    permission: 'sales.create',
    reasonKey: 'perm.cannot.createDeliveries',
    neighbours: async (id) => {
      const r = await api.deliveryNotes.neighbours({ params: { deliveryId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.deliveryNotes.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.deliveryNotes.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        deliveries: { id: string; code: string; partnerName: string; deliveryDate: string }[];
      }) => b.deliveries.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.deliveryDate }))),
  },
  creditNote: {
    route: (id) => `/sales/returns/${id}`,
    newPath: '/sales/returns/new',
    permission: 'sales.create',
    reasonKey: 'perm.cannot.createSalesReturns',
    neighbours: async (id) => {
      const r = await api.creditNotes.neighbours({ params: { creditNoteId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.creditNotes.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.creditNotes.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        creditNotes: { id: string; code: string; partnerName: string; creditDate: string }[];
      }) => b.creditNotes.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.creditDate }))),
  },
  purchaseReturn: {
    route: (id) => `/purchases/returns/${id}`,
    newPath: '/purchases/returns/new',
    permission: 'purchasing.create',
    reasonKey: 'perm.cannot.createPurchaseReturns',
    neighbours: async (id) => {
      const r = await api.purchaseReturns.neighbours({ params: { returnId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.purchaseReturns.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.purchaseReturns.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        returns: { id: string; code: string; partnerName: string; returnDate: string }[];
      }) => b.returns.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.returnDate }))),
  },
  importOrder: {
    // PUR-06's shipment: its number is the stored IMP-YYYY-### reference.
    route: (id) => `/purchases/imports/${id}`,
    newPath: '/purchases/imports/new',
    permission: 'purchasing.create',
    reasonKey: 'perm.cannot.createImports',
    neighbours: async (id) => {
      const r = await api.imports.neighbours({ params: { importId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.imports.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.imports.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        orders: { id: string; ref: string; supplierName: string; arriveDate: string }[];
      }) => b.orders.map((d) => ({ id: d.id, code: d.ref, name: d.supplierName, date: d.arriveDate }))),
  },
  item: {
    route: (id) => `/inventory/items/${id}`,
    newPath: '/inventory/items/new',
    permission: 'inventory.create',
    reasonKey: 'perm.cannot.createItems',
    neighbours: async (id) => {
      const r = await api.items.neighbours({ params: { itemId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body;
    },
    lookup: async (code) => lookupId(await api.items.lookup({ query: { code } })),
    search: async (q, p) =>
      page(await api.items.list({ query: { ...(q ? { q } : {}), page: p, ...PAGE } }), (b: {
        items: { id: string; code: string; name: string }[];
      }) => b.items.map((d) => ({ id: d.id, code: d.code, name: d.name, date: null }))),
  },
};

/**
 * "Has the person typed anything since the stored document was adopted?"
 *
 * `snapshot` is whatever state the editor considers its typed work (lines,
 * header, notes — anything a Save would send); `settledKey` names the stored
 * document the state currently mirrors, and is null while a load is in
 * flight. The baseline is re-taken whenever the key changes — the first adopt,
 * and every re-adopt after a save — so a document that was just saved reads
 * as clean without the editor having to say so.
 */
export function useUnsavedChanges(snapshot: unknown, settledKey: string | null): boolean {
  const serialized = JSON.stringify(snapshot);
  const [baseline, setBaseline] = useState<{ key: string | null; value: string }>({
    key: null,
    value: '',
  });
  useEffect(() => {
    if (baseline.key !== settledKey) setBaseline({ key: settledKey, value: serialized });
  }, [settledKey, serialized, baseline.key]);
  return settledKey !== null && baseline.key === settledKey && baseline.value !== serialized;
}

/** The rail. Rendered by `DocumentToolbar` at the head of its action group. */
export function DocumentHeaderControls({ kind, id, dirty }: DocumentHeaderSpec) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const spec = DOCUMENT_KINDS[kind];
  const rtl = i18n.dir() === 'rtl';
  const [finding, setFinding] = useState(false);
  const [goTo, setGoTo] = useState('');
  const [missed, setMissed] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);

  const { data: neighbours } = useQuery({
    queryKey: ['document-neighbours', kind, id],
    enabled: id !== undefined,
    queryFn: () => spec.neighbours(id!),
  });
  const prevId = neighbours?.prevId ?? null;
  const nextId = neighbours?.nextId ?? null;
  const canPrev = prevId !== null && !dirty;
  const canNext = nextId !== null && !dirty;

  // Alt+← / Alt+→ — reversed on an Arabic screen, where "previous" is to the
  // right exactly as the button that says so is.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!event.altKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      const backwards = (event.key === 'ArrowLeft') !== rtl;
      const target = backwards ? (canPrev ? prevId : null) : canNext ? nextId : null;
      if (target === null) return;
      event.preventDefault();
      void navigate(spec.route(target));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [rtl, canPrev, canNext, prevId, nextId, navigate, spec]);

  const onGoToKey = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const code = goTo.trim();
    if (code === '' || looking) return;
    setLooking(true);
    setMissed(null);
    try {
      const found = await spec.lookup(code);
      if (found === null) setMissed(code);
      else {
        setGoTo('');
        void navigate(spec.route(found));
      }
    } catch {
      setMissed(code);
    } finally {
      setLooking(false);
    }
  };

  // The arrows' tooltip says WHY they wait when they do — a greyed control
  // with no reason is the shape 16.7 rules out for permissions, and it is no
  // better for a rule.
  const prevTitle = dirty ? t('docnav.unsavedBlocks') : t('docnav.prev');
  const nextTitle = dirty ? t('docnav.unsavedBlocks') : t('docnav.next');

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="document-header-controls">
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t('docnav.prev')}
        title={prevTitle}
        disabled={!canPrev}
        onClick={() => prevId && void navigate(spec.route(prevId))}
      >
        <ChevronsLeft className="rtl:rotate-180" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t('docnav.next')}
        title={nextTitle}
        disabled={!canNext}
        onClick={() => nextId && void navigate(spec.route(nextId))}
      >
        <ChevronsRight className="rtl:rotate-180" aria-hidden />
      </Button>
      <div className="grid gap-0.5">
        <Input
          type="search"
          aria-label={t('docnav.goTo')}
          placeholder={t('docnav.goTo')}
          title={t('docnav.goToHint')}
          value={goTo}
          onChange={(event) => {
            setGoTo(event.target.value);
            if (missed !== null) setMissed(null);
          }}
          onKeyDown={(event) => void onGoToKey(event)}
          className="h-9 w-32 font-mono text-sm"
        />
        {missed !== null ? (
          <p role="status" className="text-xs text-destructive">
            {t('docnav.notFound', { code: missed })}
          </p>
        ) : null}
      </div>
      <Button type="button" variant="outline" onClick={() => setFinding(true)}>
        <Search aria-hidden /> {t('docnav.find')}
      </Button>
      <Gated permission={spec.permission} reasonKey={spec.reasonKey}>
        <Button asChild variant="outline">
          <Link to={spec.newPath}>
            <Plus aria-hidden /> {t('common.new')}
          </Link>
        </Button>
      </Gated>
      {finding ? <DocumentFindModal kind={kind} onClose={() => setFinding(false)} /> : null}
    </div>
  );
}

/** The kind's own list title — literal keys, so the i18n census can read them. */
function useKindTitle(kind: DocumentKind): string {
  const { t } = useTranslation();
  const titles: Record<DocumentKind, string> = {
    invoice: t('sales.title'),
    quotation: t('quotes.title'),
    salesOrder: t('orders.title'),
    bill: t('bills.title'),
    purchaseOrder: t('po.title'),
    goodsReceipt: t('grn.title'),
    deliveryNote: t('dn.title'),
    creditNote: t('returns.title'),
    purchaseReturn: t('preturn.title'),
    importOrder: t('imports.title'),
    item: t('items.title'),
  };
  return titles[kind];
}

/**
 * INV-H3 / IT-N2 — "Find": one compact, paged list of the kind inside the
 * product's shared overlay. A row is a button: click it and the editor moves
 * to that record. The SERVER narrows, for every kind: the typed text rides as
 * the list route's `q`, so the total and the pages are those of the matching
 * set. (Quotations and goods receipts used to be narrowed on the client, over
 * the one page already fetched — a search that could only find what happened
 * to be on page 1.)
 */
export function DocumentFindModal({ kind, onClose }: { kind: DocumentKind; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spec = DOCUMENT_KINDS[kind];
  const title = useKindTitle(kind);
  const [q, setQ] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const search = useDebounced(q.trim());

  const { data, isPending, isError } = useQuery({
    queryKey: ['document-find', kind, search, pageNo],
    queryFn: () => spec.search(search, pageNo),
  });

  const rows = data?.rows ?? [];
  const range = data ? pageRange({ total: data.total, page: pageNo, pageSize: data.pageSize }) : null;

  return (
    <Modal title={`${t('docnav.find')} — ${title}`} onClose={onClose} className="max-w-xl">
      <div className="grid gap-3">
        <Input
          type="search"
          aria-label={t('docnav.searchLabel')}
          placeholder={t('docnav.searchPlaceholder')}
          value={q}
          onChange={(event) => {
            setQ(event.target.value);
            setPageNo(1);
          }}
          className="h-10"
        />
        {isPending ? (
          <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy />
        ) : isError ? (
          <p role="alert" className="py-6 text-center text-sm text-destructive">
            {t('common.loadFailed')}
          </p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('docnav.noMatches')}</p>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-3 py-2 text-start text-sm hover:bg-muted"
                  onClick={() => {
                    onClose();
                    void navigate(spec.route(row.id));
                  }}
                >
                  <Ltr value={row.code} className="w-28 shrink-0 font-mono text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                  {row.date ? <Ltr value={row.date} className="shrink-0 font-mono text-xs text-muted-foreground" /> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
        {range && data && data.total > 0 ? (
          <Pager
            page={pageNo}
            pages={range.pages}
            first={range.first}
            last={range.last}
            total={data.total}
            onPage={setPageNo}
            label={title}
          />
        ) : null}
      </div>
    </Modal>
  );
}
